/**
 * AX-Agent 화면 카탈로그 — navigate 인텐트의 이동 대상 목록.
 *
 * 설계: axadmin docs/designs/mes-agent-screen-interaction.md §5.2.
 * 화면 제목·경로·권한의 SSOT 는 Menu 테이블(사이드바와 동일 소스)이므로
 * 카탈로그는 메뉴 트리에서 생성한다. 식별자(name)는 app path — 라우트
 * name 은 페이지마다 있지만 메뉴가 없는 라우트는 이동 대상도 아니다.
 *
 * 권한 필터는 여기(카탈로그 노출)와 navigate 수신(이동 실행) 양쪽에서
 * 수행한다 — 패널·에이전트 BE 는 신뢰 경계 밖 (§5.6).
 */
import type { MenuTreeNode } from '@axsolutionsdev/shared';

/** ax-agent embed-sdk `AxScreenEntry` 미러 (core-fe 는 embed-sdk 에 의존하지 않는다) */
export interface AgentScreenEntry {
  /** 이동 식별자 — app path (`/mes/items`). ax:navigate.target 으로 그대로 돌아온다. */
  name: string;
  path: string;
  title: string;
  keywords?: string[];
}

const MAX_SCREENS = 500;

export function buildAgentCatalog(
  menus: MenuTreeNode[],
  hasPermission: (code: string) => boolean,
): AgentScreenEntry[] {
  const entries: AgentScreenEntry[] = [];
  const seen = new Set<string>();

  function walk(nodes: MenuTreeNode[], parents: string[]): void {
    for (const node of nodes) {
      if (!node.isVisible || !node.isEnabled) continue;
      if (node.permission && !hasPermission(node.permission)) continue;
      if (node.path && !seen.has(node.path) && entries.length < MAX_SCREENS) {
        seen.add(node.path);
        // 상위 그룹명을 키워드로 — "품질 > 검사요청" 을 "품질 검사"로도 찾게 한다
        const keywords = [node.code, ...parents].filter(Boolean);
        entries.push({ name: node.path, path: node.path, title: node.name, keywords });
      }
      if (node.children.length) walk(node.children, [...parents, node.name]);
    }
  }

  walk(menus, []);
  return entries;
}
